import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
} from '@angular/router';
import { AuthService } from '@services/auth.service';
import { ConfigService } from '@services/config.service';
import { FacebookPixelService } from '@services/facebook-pixel.service';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { RoutingInterceptorService } from '@services/routing-interceptor.service';
import { TiktokPixelService } from '@services/tiktok-pixel.service';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  isLoading: boolean = true;
  popupSubscription: Subscription | undefined;
  unsubscribe = new Subject<void>();

  constructor(
    private facebookPixelService: FacebookPixelService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private routingInterceptorService: RoutingInterceptorService,
    private authService: AuthService,
    private router: Router,
    private ccService: NgcCookieConsentService,
    private tiktokPixelService: TiktokPixelService,
    private configService: ConfigService
  ) {
    this.routingInterceptorService.init();
    this.router.events
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((routerEvent: any) => {
        this.checkRouterEvent(routerEvent as RouterEvent);
      });
  }

  ngOnInit(): void {
    this.setFavicon();
    this.setDesign();
    this.setSiteTitle();
    this.googleAnalyticsService.init();
    this.facebookPixelService.init();
    this.tiktokPixelService.init();

    this.popupSubscription = this.ccService.popupClose$.subscribe(() => {
      this.googleAnalyticsService.init();
      this.facebookPixelService.init();
      this.tiktokPixelService.init();
    });

    this.authService.setUserLoggedIn();
  }

  checkRouterEvent(routerEvent: RouterEvent): void {
    if (routerEvent instanceof NavigationStart) {
      this.isLoading = true;
    }

    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      this.isLoading = false;
    }
  }

  setDesign() {
    const color = this.configService.settings?.primary_color_rgb;

    if (color) {
      document.documentElement.style.setProperty(
        '--bs-primary',
        'rgb(' + color + ')'
      );
      document.documentElement.style.setProperty(
        '--bs-primary-rgb',
        color
      );
    }
  }

  setFavicon() {
    const faviconUrl = this.configService.settings?.favicon_url;

    if (faviconUrl) {
      var link = document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'icon';
      link.href = faviconUrl;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }

  setSiteTitle() {
    const siteTitle = this.configService.settings?.site_title;
    
    if (siteTitle) {
      document.title = siteTitle;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.popupSubscription?.unsubscribe();
  }
}
